// The trip somebody is planning, and the four rules that decide what is in it.
//
// WHY A MODULE-LEVEL REF RATHER THAN THE ROUTE. This app's router uses
// `createMemoryHistory`, which carries no `state` — there is nowhere to put an
// itinerary on a push, and `/tabs/journey/2` is an index into something that
// has to already exist. So the planned trip lives here: the departures board
// and the Options screen write it, and the Journey screen reads it. (There was
// a Trip tab that planned into it too; the board with a destination set does
// that job now.)
//
// It is NOT persisted. A plan is a statement about the next hour, and the one
// thing worse than no plan is yesterday's plan presented as today's.
//
// Everything else here is pure and tested in `tests/directions-state.test.js`,
// with the single exception of the two `prefs` readers — recent destinations
// outlive the session, because the walk home is the walk in, reversed.

import { ref } from 'vue';
import { prefs } from './core/storage.js';
import { currentCity, DEFAULT_CITY } from './city.js';
import { NEAR_METRES, stationById } from './network.js';

/** Where the last few destinations live. Ids, not records: the names change. */
export const RECENT_KEY = 'lateagain:recent-destinations';

/**
 * The recents key for the city in force. Melbourne keeps the bare one.
 *
 * A stop id means nothing in another network, so the walk home somebody keeps
 * going back to is Melbourne's walk home and not Sydney's. Read per call
 * rather than held, because a city switch is a plain toggle now and there is
 * nothing here to re-point on it.
 *
 * Melbourne's suffix is empty on purpose: `prefs['lateagain:recent-destinations']`
 * is where every recent already on a phone is, and this is what leaves it
 * exactly where it was.
 */
const recentKey = () => {
  const id = currentCity();
  return id === DEFAULT_CITY ? RECENT_KEY : `${RECENT_KEY}:${id}`;
};

/**
 * Where the last few stations somebody LEFT FROM live.
 *
 * A second list, not a second use of the first one. The two questions have
 * different answers and mixing them makes both lists wrong: somebody whose
 * destinations are Flinders Street and Melbourne Central does not want those
 * offered as the board to switch to, and somebody who switched the board to
 * Richmond this morning is not thereby travelling to Richmond.
 *
 * NO BARE-KEY EXCEPTION. There is no origins list already on anybody's phone,
 * so this one carries its city in the key even in Melbourne — the destinations
 * key keeps its empty suffix for the opposite reason.
 */
export const RECENT_ORIGINS_KEY = 'lateagain:recent-origins';

const originsKey = () => `${RECENT_ORIGINS_KEY}:${currentCity()}`;

/**
 * How many destinations are remembered.
 *
 * Five is a home station, a work station, and the three places somebody
 * actually went this month. Twenty would be a search history, which is a list
 * nobody reads and a record nobody asked us to keep.
 */
export const RECENT_CAP = 5;

/**
 * How long a plan is "now".
 *
 * The same minute the departure board uses, and for the same reason: past it a
 * time on screen is a claim about the past wearing the present tense. Re-entry
 * past this re-plans; inside it, nothing does. There is NO TIMER anywhere in
 * Directions — see the call budget in IDEATION.md.
 */
export const REPLAN_AFTER_MS = 60_000;

/** The shape, in one place, so a reset cannot forget a field. */
export const EMPTY_TRIP = Object.freeze({
  from: null,
  to: null,
  /** 'nearby' | 'default' | 'chosen' — only 'chosen' survives a new fix. */
  fromSource: null,
  fromMetres: null,
  options: [],
  plannedAt: null,
  error: null,
  /**
   * True when the failure, or the shortness of the plan, is this app's own
   * budget rather than anything being wrong.
   *
   * A SEPARATE FIELD AND NOT A MESSAGE THE SCREEN MATCHES ON. The server
   * distinguishes a budget refusal from an outage on purpose (503 `busy`
   * against 502), and a screen that told the two apart by reading the words in
   * `error` would lose the distinction the first time the wording changed.
   */
  busy: false,
  /**
   * True when the plan STOPPED because it had waited long enough.
   *
   * A different fact from `busy` and it is kept apart for the same reason
   * `busy` is kept apart from `error`: busy is this app rationing itself and
   * is over in seconds, slow is the plan having asked somebody to watch a
   * loader for as long as anybody should be asked to. With options on the card
   * it is a line under them; with none it is the only honest thing the screen
   * can say, and it is not "no trains".
   */
  slow: false,
  reason: null,
});

export const trip = ref({ ...EMPTY_TRIP });

/** Merge into the stash. Replaces the object, so watchers on `trip` fire. */
export function setTrip(patch) {
  trip.value = { ...trip.value, ...patch };
}

// ── What to keep off the plan ────────────────────────────────────────────────
//
// A line or a station somebody named, and the departures board plans around
// them. These lived in the Trip tab as two component refs; the board plans
// now, so they live here, where the board and anything it opens read the same
// two lists.
//
// NOT PERSISTED, for the reason the trip is not: "keep off the Frankston line"
// is about this evening, and yesterday's constraint silently bending
// tomorrow's board would be a plan nobody asked for. Cleared on a city switch
// (see `switchCity` in app-state.js) because a Frankston route id is not a
// Sydney one.

/** Route ids and stop ids somebody named to go around. Both arrays of numbers. */
export const avoidRoutes = ref([]);
export const avoidStops = ref([]);

const cleanIds = (list) => [...new Set((Array.isArray(list) ? list : [])
  .map(Number)
  .filter((n) => Number.isFinite(n) && n > 0))];

/** Replace one half or both. Anything not a positive id is dropped. */
export function setAvoid({ routeIds, stopIds } = {}) {
  if (routeIds !== undefined) avoidRoutes.value = cleanIds(routeIds);
  if (stopIds !== undefined) avoidStops.value = cleanIds(stopIds);
}

export function clearAvoid() {
  avoidRoutes.value = [];
  avoidStops.value = [];
}

/**
 * The avoid set as `planTrip` takes it, or null when there is nothing in it.
 *
 * Null rather than two empty lists, so the no-avoidance path through the
 * planner is the path it has always been.
 */
export function avoidFor(routeIds = avoidRoutes.value, stopIds = avoidStops.value) {
  const r = cleanIds(routeIds);
  const s = cleanIds(stopIds);
  if (!r.length && !s.length) return null;
  return { routeIds: r, stopIds: s };
}

/**
 * One string per avoid set, order-free: `"4,11:1071"`. Empty for none.
 *
 * What a plan cache is keyed on alongside its two ends — a plan made around
 * the Sunbury line is not an answer to a question that did not mention it.
 */
export function avoidKeyOf(avoid) {
  if (!avoid) return '';
  const r = cleanIds(avoid.routeIds).sort((a, b) => a - b).join(',');
  const s = cleanIds(avoid.stopIds).sort((a, b) => a - b).join(',');
  return r || s ? `${r}:${s}` : '';
}

/**
 * The three fields a FROM or a TO needs, whatever shape it arrived in.
 *
 * Saved rows (`src/store.js`) carry `lines` as the string "4,11"; bundled
 * network records carry it as an array; the picker hands over either. Nothing
 * downstream wants the lines — `planTrip` takes stop ids — so the difference is
 * dropped here rather than leaking into the page as two shapes for one thing.
 */
export function place(station) {
  if (!station) return null;
  const stopId = Number(station.stopId);
  if (!Number.isFinite(stopId) || stopId <= 0) return null;
  return {
    stopId,
    name: String(station.name ?? '').trim(),
    suburb: String(station.suburb ?? '').trim(),
  };
}

/**
 * A stop id pushed onto the front of a recents list. Newest first, no repeats.
 *
 * Pure, and the caller owns the storage — the same list is written by choosing
 * a destination and by reversing a trip, and a function that both computes and
 * persists cannot be checked without a browser.
 */
export function pushRecent(list, stopId, cap = RECENT_CAP) {
  const clean = (Array.isArray(list) ? list : [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  const id = Number(stopId);
  if (!Number.isFinite(id) || id <= 0) return clean.slice(0, Math.max(0, cap));
  return [id, ...clean.filter((n) => n !== id)].slice(0, Math.max(0, cap));
}

/**
 * A stored list of ids, as station records, minus one.
 *
 * `stationById` is the filter that matters: an id belongs to the bundle it was
 * written under, and a Melbourne stop read against Sydney's network is a row
 * with no name. Per-city keys mean that should not happen; this means it
 * cannot show if it does.
 */
function readRecent(key, excludeStopId) {
  const raw = prefs[key];
  return (Array.isArray(raw) ? raw : [])
    .map((id) => stationById(id))
    .filter(Boolean)
    .filter((s) => Number(s.stopId) !== Number(excludeStopId));
}

/** Push one id onto a stored list, newest first, no repeats. */
function writeRecent(key, station) {
  const id = Number(station?.stopId ?? station);
  if (!Number.isFinite(id) || id <= 0) return;
  const raw = prefs[key];
  prefs[key] = pushRecent(Array.isArray(raw) ? raw : [], id);
}

/** The remembered destinations as station records, minus wherever you are. */
export function recentDestinations(excludeStopId = null) {
  return readRecent(recentKey(), excludeStopId);
}

/** The remembered origins as station records, minus the one already showing. */
export function recentOrigins(excludeStopId = null) {
  return readRecent(originsKey(), excludeStopId);
}

/**
 * Remember a station somebody left from.
 *
 * Called when a board is SWITCHED or a trip's FROM is chosen — never when one
 * arrives from a nearby chip or from the saved default. Those two are the app
 * deciding; this list is meant to be the record of what the person did, and a
 * list the app writes to itself is a list that stops being a shortcut.
 */
export function rememberOrigin(station) {
  writeRecent(originsKey(), station);
}

/** Remember one. Called when a destination is chosen, not when it is typed. */
export function rememberDestination(station) {
  writeRecent(recentKey(), station);
}

/**
 * Which station the plan leaves from, before anybody has chosen one.
 *
 * Two answers and a refusal. Standing within `NEAR_METRES` of a station is
 * evidence; the saved default is a habit; anything further away is a GUESS, and
 * a trip planned from the wrong end of somebody's commute is worse than a
 * screen asking where they are. So a fix at 1.2 km does not win — it loses to
 * the default and, with no default, to nothing at all.
 *
 * Never a silent overwrite: the caller applies this only while `fromSource` is
 * not `'chosen'`.
 */
export function chooseFrom({ nearby, savedDefault } = {}) {
  const near = Array.isArray(nearby) ? nearby[0] : nearby;
  const metres = Number(near?.metres);
  if (near?.station && Number.isFinite(metres) && metres <= NEAR_METRES) {
    return { station: near.station, source: 'nearby', metres };
  }
  if (savedDefault) return { station: savedDefault, source: 'default', metres: null };
  return null;
}

/** Whether what is on screen has stopped being now. Unplanned counts as stale. */
export function needsReplan(state, now = Date.now()) {
  const at = Number(state?.plannedAt);
  if (!Number.isFinite(at) || at <= 0) return true;
  return now - at > REPLAN_AFTER_MS;
}
